import React, { useEffect, useState } from "react";
import { API } from "@/lib/api";
import { FilePdf, ArrowsClockwise, Warning, ArrowSquareOut } from "@phosphor-icons/react";

/**
 * Fetches an authenticated PDF and shows it inline via an <iframe> pointing to
 * a blob URL. Revokes the URL on unmount to avoid memory leaks.
 *
 * Props:
 *   endpoint  — string, e.g. "/rejection-records/<id>/pdf"
 *   height    — number of px (default 720)
 */
export default function PdfPreview({ endpoint, height = 720, testId = "pdf-preview" }) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    let created = "";
    try {
      const token = localStorage.getItem("rcn_token");
      const res = await fetch(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setError(`Servidor respondió ${res.status}`);
        return;
      }
      const blob = await res.blob();
      if (blob.type && blob.type !== "application/pdf") {
        setError("El servidor no devolvió un PDF válido");
        return;
      }
      created = URL.createObjectURL(blob);
      setUrl(created);
    } catch (err) {
      console.debug("[PdfPreview] fetch failed", err);
      setError("No se pudo cargar la vista previa");
    } finally {
      setLoading(false);
    }
    return () => created && URL.revokeObjectURL(created);
  };

  useEffect(() => {
    let revoke;
    (async () => {
      revoke = await load();
    })();
    return () => { if (typeof revoke === "function") revoke(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  return (
    <div className="bg-[#111111] border border-white/10 rounded-sm overflow-hidden" data-testid={testId}>
      <div className="px-4 py-2.5 border-b border-white/10 flex items-center gap-2">
        <FilePdf size={16} weight="fill" className="text-[#FF3B30]" />
        <div className="font-mono-data text-[11px] uppercase tracking-widest text-white/80 flex-1">
          Vista previa PDF
        </div>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[10px] font-mono-data uppercase tracking-widest text-[#0055FF] hover:text-white"
            data-testid={`${testId}-open`}
          >
            Abrir en pestaña <ArrowSquareOut size={11} />
          </a>
        )}
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1 text-[10px] font-mono-data uppercase tracking-widest text-white/50 hover:text-white"
          data-testid={`${testId}-refresh`}
        >
          <ArrowsClockwise size={11} /> Recargar
        </button>
      </div>
      <div style={{ height }} className="bg-[#0A0A0A]">
        {loading ? (
          <div className="w-full h-full flex items-center justify-center text-white/50 text-sm">
            Cargando PDF…
          </div>
        ) : error ? (
          <div className="w-full h-full flex flex-col items-center justify-center text-[#FF3B30] text-sm gap-2">
            <Warning size={22} weight="fill" />
            {error}
          </div>
        ) : (
          <iframe
            src={url}
            title="Vista previa"
            className="w-full h-full"
            style={{ border: "none", background: "white" }}
          />
        )}
      </div>
    </div>
  );
}
